import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';
import { useUserInitQuery } from '@/hooks/queries/useUserInitQuery';

interface Balance {
  gc_balance: number;
  sc_bonus: number;
  sc_purchased: number;
  sc_redeemable: number;
}

interface BalanceContextType {
  balance: Balance | null;
  isLoading: boolean;
  refreshBalance: () => Promise<void>;
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};

export const BalanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<Balance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Use unified init query for initial data (avoids separate API call)
  const { data: initData, isLoading: initLoading } = useUserInitQuery();

  // Update balance from unified init data when available
  useEffect(() => {
    if (initData?.balance && !hasInitialLoad) {
      setBalance({
        gc_balance: Number(initData.balance.gc_amount) || 0,
        sc_bonus: Number(initData.balance.sc_bonus_amount) || 0,
        sc_purchased: Number(initData.balance.sc_promotional_amount) || 0,
        sc_redeemable: Number(initData.balance.sc_redeemable_amount) || 0,
      });
      setIsLoading(false);
      setHasInitialLoad(true);
    }
  }, [initData, hasInitialLoad]);

  const fetchBalance = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      // Only show loading on initial load
      if (!hasInitialLoad) {
        setIsLoading(true);
      }

      const data = await apiClient.get('/profile/balance');
      
      if (data?.balance) {
        setBalance({
          gc_balance: Number(data.balance.gc_amount) || 0,
          sc_bonus: Number(data.balance.sc_bonus_amount) || 0,
          sc_purchased: Number(data.balance.sc_promotional_amount) || 0,
          sc_redeemable: Number(data.balance.sc_redeemable_amount) || 0,
        });
      }
    } catch {
      // Error fetching balance
    } finally {
      setIsLoading(false);
      setHasInitialLoad(true);
    }
  }, [user, hasInitialLoad]);

  // Effect for initial balance fetch (only if not already loaded from init query)
  useEffect(() => {
    if (!user) {
      setBalance(null);
      setIsLoading(false);
      setHasInitialLoad(false);
      return;
    }

    // Skip initial fetch if we already have data from unified init
    if (hasInitialLoad) return;
    
    // Only fetch directly if init query didn't provide data
    if (!initLoading && !initData?.balance) {
      fetchBalance();
    }
  }, [user, fetchBalance, hasInitialLoad, initLoading, initData]);

  // Separate effect for WebSocket subscription - must run regardless of hasInitialLoad
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    let unsubscribe: (() => void) | null = null;

    import('@/lib/websocket-client').then(({ websocketClient }) => {
      websocketClient.connect(token).catch(() => {});
      
      unsubscribe = websocketClient.subscribe('balance:update', (data: any) => {
        // Safely convert all values to numbers, ensuring they're valid
        const safeNumber = (val: any): number => {
          const num = Number(val);
          return typeof num === 'number' && !isNaN(num) ? num : 0;
        };
        
        setBalance({
          gc_balance: safeNumber(data?.gc_amount ?? data?.gc_balance),
          sc_bonus: safeNumber(data?.sc_bonus_amount ?? data?.sc_bonus),
          sc_purchased: safeNumber(data?.sc_promotional_amount ?? data?.sc_purchased),
          sc_redeemable: safeNumber(data?.sc_redeemable_amount ?? data?.sc_redeemable),
        });
      });
    });

    // Periodic balance refresh as fallback (every 30 seconds)
    const balanceRefreshInterval = setInterval(() => {
      fetchBalance();
    }, 30000);

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      clearInterval(balanceRefreshInterval);
    };
  }, [user, fetchBalance]);

  return (
    <BalanceContext.Provider value={{ balance, isLoading, refreshBalance: fetchBalance }}>
      {children}
    </BalanceContext.Provider>
  );
};

